import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import VideoCard from "@/components/base/VideoCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { videos } from "@/mocks/videos";

const hotCategories = [
  { label: "电影", to: "/category/movie", icon: "ri-film-line" },
  { label: "电视剧", to: "/category/drama", icon: "ri-tv-2-line" },
  { label: "综艺", to: "/category/variety", icon: "ri-mic-line" },
  { label: "短视频", to: "/category/short", icon: "ri-video-line" },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q")?.trim() ?? "";
  const [input, setInput] = useState(query);

  usePageMeta({
    title: query ? `搜索“${query}” - 在线视频` : "搜索视频 - 在线视频",
    description: query
      ? `在线视频为您找到与“${query}”相关的视频内容。`
      : "搜索电影、电视剧、综艺和短视频。",
    keywords: "在线视频,视频搜索,在线观看",
  });

  useEffect(() => {
    setInput(query);
  }, [query]);

  const results = useMemo(() => {
    if (!query) return [];
    const kw = query.toLowerCase();
    return videos.filter(
      (v) =>
        v.title.toLowerCase().includes(kw) ||
        v.subtitle.toLowerCase().includes(kw) ||
        v.tags.some((t) => t.toLowerCase().includes(kw)) ||
        v.region.toLowerCase().includes(kw),
    );
  }, [query]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    if (!q) return;
    setSearchParams({ q });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full px-4 md:px-8 py-8">
        <div className="max-w-[1200px] mx-auto">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
            搜索视频
          </h1>

          {/* Search box */}
          <form onSubmit={handleSubmit} className="mb-8">
            <div className="flex flex-col sm:flex-row gap-3 max-w-2xl">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  type="text"
                  name="q"
                  placeholder="输入视频标题、类型或地区关键词"
                  className="w-full pl-11 pr-4 py-3 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                />
              </div>
              <button
                type="submit"
                className="px-7 py-3 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                搜索
              </button>
            </div>
          </form>

          {query && (
            <p className="text-sm text-foreground-600 mb-6">
              关键词
              <span className="mx-1 px-2 py-0.5 rounded bg-primary-50 text-primary-700 font-medium">
                {query}
              </span>
              共找到
              <span className="mx-1 font-semibold text-foreground-900">
                {results.length}
              </span>
              个结果
            </p>
          )}

          {!query && (
            <div className="flex flex-col items-center justify-center py-20 px-6 rounded-lg bg-background-50 border border-dashed border-background-300 text-center">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                <i className="ri-search-eye-line text-3xl"></i>
              </span>
              <h2 className="mt-5 text-lg font-semibold text-foreground-800">
                输入关键词开始搜索
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                试试搜索「星际」「悬疑」「综艺」等关键词。
              </p>
            </div>
          )}

          {query && results.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
              {results.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          )}

          {query && results.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 px-6 rounded-lg bg-background-50 border border-dashed border-background-300 text-center">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-file-search-line text-3xl"></i>
              </span>
              <h2 className="mt-5 text-lg font-semibold text-foreground-800">
                没有找到与「{query}」相关的内容
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                换个关键词试试，或从下面的热门分类开始探索。
              </p>
              <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3 w-full max-w-2xl">
                {hotCategories.map((c) => (
                  <Link
                    key={c.to}
                    to={c.to}
                    className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                      <i className={`${c.icon} text-xl`}></i>
                    </span>
                    <span className="text-sm font-medium text-foreground-800">
                      {c.label}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}